import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import usePageTitle from "../hooks/usePageTitle";
import { FaLaptop, FaPlus, FaTimes, FaCalendarAlt, FaClock, FaMapMarkerAlt, FaInfoCircle } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { getToken } from '../utils/api';
import useRolePermissions, { hasPermission } from '../hooks/useRolePermissions';

// Helper to compare dates
function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

// Helper to get calendar days for a month
function getCalendarDays(date) {
  const year = date.getFullYear();
  const month = date.getMonth();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Get the first day of the month
  const firstDay = new Date(year, month, 1);
  // Get the last day of the month
  const lastDay = new Date(year, month + 1, 0);

  // Get the day of the week for the first day (0 = Sunday, 1 = Monday, etc.)
  const firstDayOfWeek = firstDay.getDay();

  // Get the last day of the previous month
  const prevMonthLastDay = new Date(year, month, 0);
  const daysInPrevMonth = prevMonthLastDay.getDate();

  // Get the number of days in the current month
  const daysInMonth = lastDay.getDate();

  // Calculate the number of days from the previous month to show
  const daysFromPrevMonth = firstDayOfWeek;

  // Calculate the number of days from the next month to show
  const daysFromNextMonth = 42 - (daysFromPrevMonth + daysInMonth); // 6 rows x 7 days = 42

  const days = [];

  // Add days from previous month
  for (let i = daysFromPrevMonth - 1; i >= 0; i--) {
    const day = new Date(year, month - 1, daysInPrevMonth - i);
    days.push({
      date: day,
      isCurrentMonth: false,
      isToday: false,
      isWeekend: [0, 6].includes(day.getDay()),
      dateNum: day.getDate()
    });
  }

  // Add days from current month
  for (let i = 1; i <= daysInMonth; i++) {
    const day = new Date(year, month, i);
    const dayStart = new Date(day);
    dayStart.setHours(0, 0, 0, 0);

    days.push({
      date: day,
      isCurrentMonth: true,
      isToday: dayStart.getTime() === today.getTime(),
      isWeekend: [0, 6].includes(day.getDay()),
      dateNum: i
    });
  }

  // Add days from next month
  for (let i = 1; i <= daysFromNextMonth; i++) {
    const day = new Date(year, month + 1, i);
    days.push({
      date: day,
      isCurrentMonth: false,
      isToday: false,
      isWeekend: [0, 6].includes(day.getDay()),
      dateNum: i
    });
  }

  return days;
}

// Map of Tailwind color classes to their hex values with a more vibrant palette
const colorMap = {
  'bg-violet-500': '#6366F1',
  'bg-blue-500': '#3B82F6',
  'bg-cyan-500': '#06B6D4',
  'bg-teal-500': '#14B8A6',
  'bg-emerald-500': '#10B981',
  'bg-green-500': '#22C55E',
  'bg-lime-500': '#84CC16',
  'bg-yellow-500': '#EAB308',
  'bg-amber-500': '#F59E0B',
  'bg-orange-500': '#F97316',
  'bg-red-500': '#EF4444',
  'bg-pink-500': '#EC4899',
  'bg-rose-500': '#F43F5E',
  'bg-purple-500': '#A855F7',
  'bg-violet-500': '#8B5CF6',
  'bg-fuchsia-500': '#D946EF'
};

// Array of Tailwind color classes for events
const colorOptions = Object.keys(colorMap);

// Function to get a random color from the options
const getRandomColor = () => {
  return colorOptions[Math.floor(Math.random() * colorOptions.length)];
};

// Function to get a contrasting text color (black or white) based on background color
const getContrastColor = (bgColor) => {
  // These colors look better with white text
  const darkColors = ['bg-blue-500', 'bg-violet-500', 'bg-violet-500', 'bg-purple-500', 'bg-fuchsia-500', 'bg-pink-500', 'bg-rose-500'];
  return darkColors.includes(bgColor) ? 'bg-white' : 'bg-gray-900';
};

// Function to fetch birthdays from the backend API
const fetchBirthdays = async () => {
  try {
    // Was reading the wrong localStorage key ('token' -- the real login
    // token is stored under 'portal_token' by utils/api.js's setToken()),
    // so this was always sending an empty/invalid Authorization header.
    // getToken() is the single source of truth for that key.
    const token = getToken();
    const response = await fetch('https://dev.sliitleo.org/Backend/membersBirthdays.php', {
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json'
      }
    });
    const text = await response.text();
    try {
      return JSON.parse(text).data;
    } catch (jsonErr) {
      console.error('Raw response from membersBirthdays.php:', text);
      throw jsonErr;
    }
  } catch (error) {
    console.error('Error fetching birthdays:', error);
    return [];
  }
};

// Function to fetch events from the backend API
const fetchEvents = async (startDate, endDate) => {
  try {
    // Was reading the wrong localStorage key ('token' -- the real login
    // token is stored under 'portal_token' by utils/api.js's setToken()),
    // so this was always sending an empty/invalid Authorization header.
    // getToken() is the single source of truth for that key.
    const token = getToken();
    const start = format(startDate, 'yyyy-MM-dd');
    const end = format(endDate, 'yyyy-MM-dd');

    // Build the URL with query parameters
    const url = new URL('https://dev.sliitleo.org/Backend/get_events.php');
    url.searchParams.append('start', start);
    url.searchParams.append('end', end);

    // Prepare headers
    const headers = {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    };

    // Add authorization header if token exists
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: headers,
      mode: 'cors',
      cache: 'no-cache'
    });

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorMessage;
      } catch (e) {
        // If we can't parse JSON, use the status text
        errorMessage = response.statusText || errorMessage;
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();

    // Map the API response to match the expected event format
    return data.map(event => {
      const startDate = new Date(event.start);
      const endDate = new Date(event.end || event.start);
      const isAllDay = event.allDay || (!event.start.includes('T') && !event.end?.includes('T'));

      // Get a random color from colorMap for each event
      const colorKeys = Object.keys(colorMap);
      const randomColor = colorKeys[Math.floor(Math.random() * colorKeys.length)];

      return {
        ...event,
        id: event.id.toString(),
        title: event.title || 'No Title',
        date: startDate,
        endDate: endDate,
        start: event.start,
        end: event.end || event.start,
        allDay: isAllDay,
        time: isAllDay ? 'All Day' :
          (startDate.getHours() ? format(startDate, 'h:mm a') : '') +
          (endDate.getHours() ? ` - ${format(endDate, 'h:mm a')}` : ''),
        description: event.description || '',
        location: event.location || '',
        color: randomColor || 'bg-violet-500',
        dotColor: getContrastColor(randomColor || 'bg-violet-500')
      };
    });
  } catch (error) {
    console.error('Error fetching events:', error);
    return [];
  }
};

// Function to create a new event
const createEvent = async (eventData) => {
  try {
    // Was reading the wrong localStorage key ('token' -- the real login
    // token is stored under 'portal_token' by utils/api.js's setToken()),
    // so this was always sending an empty/invalid Authorization header.
    // getToken() is the single source of truth for that key.
    const token = getToken();
    const response = await fetch('https://dev.sliitleo.org/Backend/calendar.php', {
      method: 'POST',
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        title: eventData.title,
        description: eventData.description || '',
        start_date: eventData.date,
        end_date: eventData.date,
        start_time: eventData.startTime,
        end_time: eventData.endTime,
        all_day: false,
        location: eventData.location || '',
        color: eventData.color || 'bg-violet-500'
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to create event');
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error creating event:', error);
    throw error;
  }
};

// Function to update an event
const updateEvent = async (eventId, eventData) => {
  try {
    if (!eventId) throw new Error('No event ID provided for update.');
    // Was reading the wrong localStorage key ('token' -- the real login
    // token is stored under 'portal_token' by utils/api.js's setToken()),
    // so this was always sending an empty/invalid Authorization header.
    // getToken() is the single source of truth for that key.
    const token = getToken();
    const response = await fetch(`https://dev.sliitleo.org/Backend/calendar.php?id=${eventId}`, {
      method: 'PUT',
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(eventData)
    });

    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const text = await response.text();
      throw new Error('Server returned non-JSON response: ' + text.slice(0, 200));
    }

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to update event');
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error updating event:', error);
    throw error;
  }
};

// Function to delete an event
const deleteEvent = async (eventId) => {
  try {
    // Was reading the wrong localStorage key ('token' -- the real login
    // token is stored under 'portal_token' by utils/api.js's setToken()),
    // so this was always sending an empty/invalid Authorization header.
    // getToken() is the single source of truth for that key.
    const token = getToken();
    const response = await fetch(`https://dev.sliitleo.org/Backend/calendar.php?id=${eventId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to delete event');
    }

    return true;
  } catch (error) {
    console.error('Error deleting event:', error);
    throw error;
  }
};

// Convert member data to calendar events (from membersBirthdays.php)
const getBirthdayEvents = (members) => {
  const currentYear = new Date().getFullYear();
  return members
    .filter(member => member.DOB)
    .map(member => {
      try {
        const birthDate = new Date(member.DOB);
        if (isNaN(birthDate.getTime())) return null;
        const birthDay = birthDate.getDate();
        const birthMonth = birthDate.getMonth();
        const thisYearsBirthday = new Date(currentYear, birthMonth, birthDay);
        return {
          id: `birthday-${member.id}`,
          title: `${member.name}'s Birthday${member.role ? ` (${member.role})` : ''}`,
          date: thisYearsBirthday,
          time: 'All Day',
          description: 'Birthday',
          location: '',
          isBirthday: true,
          member: member,
          color: 'bg-pink-500',
          dotColor: getContrastColor('bg-pink-500')
        };
      } catch (error) {
        return null;
      }
    })
    .filter(Boolean);
};

// Sample events data
const sampleEvents = [
  {
    date: new Date(2024, 6, 9),
    title: '6209 Nikolaus Club, Tayaland 92911-1095',
    time: '09:00 - 12:00',
    description: 'Monday, Feb 15',
    location: 'Main Hall',
    color: 'bg-violet-500'
  },
  {
    date: new Date(2025, 6, 14),
    title: '6209 Nikolaus Club, Tayaland 92911-1095',
    time: '10:00 - 15:00',
    description: 'Community service',
    location: 'City Park',
    color: 'bg-green-500'
  },
  {
    date: new Date(2025, 6, 19),
    title: '6209 Nikolaus Club, Tayaland 92911-1095',
    time: '13:00 - 16:00',
    description: 'Leadership skills',
    location: 'Conference Room',
    color: 'bg-blue-500'
  },
  {
    date: new Date(2025, 6, 29),
    title: '6209 Nikolaus Club, Tayaland 92911-1095',
    time: '18:00 - 22:00',
    description: 'End of month party',
    location: 'Club House',
    color: 'bg-purple-500'
  }
].map(event => ({
  ...event,
  dotColor: getContrastColor(event.color)
}));

// Get background color and style for a date based on events
const getDateStyle = (date, events) => {
  const dayEvents = events.filter(event => isSameDay(event.date, date));

  if (dayEvents.length > 0) {
    // For multiple events, create a gradient or pattern
    if (dayEvents.length > 1) {
      const colors = dayEvents.map(event => colorMap[event.color] || '#6366F1');
      return {
        background: `linear-gradient(135deg, ${colors.join(', ')})`,
        backgroundSize: '200% 200%',
        animation: 'gradient 4s ease infinite',
        position: 'relative',
        zIndex: 1,
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.2), 0 2px 4px -1px rgba(0, 0, 0, 0.1)',
        transform: 'translateY(-2px)',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.2), 0 4px 6px -2px rgba(0, 0, 0, 0.1)'
        }
      };
    }
    // For single event, use solid color with shadow
    return {
      backgroundColor: colorMap[dayEvents[0].color] || '#6366F1',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      transform: 'translateY(-2px)',
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.2), 0 4px 6px -2px rgba(0, 0, 0, 0.1)'
      }
    };
  }
  return {
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      transform: 'translateY(-2px)'
    }
  };
};

// Event modal component
const EventModal = ({ isOpen, onClose, onSave, selectedDate = new Date(), isSaving = false }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: format(selectedDate, 'yyyy-MM-dd'),
    startTime: '09:00',
    endTime: '10:00',
    location: '',
    color: colorOptions[Math.floor(Math.random() * colorOptions.length)]
  });

  const modalRef = useRef();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formData,
      date: new Date(formData.date + 'T' + formData.startTime)
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <motion.div
        ref={modalRef}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        transition={{ duration: 0.2 }}
        className="modal-panel panel"
      >
        <div className="modal-head">
          <h3>New Event</h3>
          <button type="button" onClick={onClose} className="icon-btn" aria-label="Close">
            <FaTimes size={14} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="field">
              <label className="field-label" htmlFor="cc-event-title">
                Event Title <span className="req">*</span>
              </label>
              <input
                id="cc-event-title"
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="e.g. Club General Meeting"
                required
              />
            </div>

            <div className="field">
              <label className="field-label" htmlFor="cc-event-description">
                <FaInfoCircle size={11} style={{ marginRight: 6, opacity: 0.75 }} />
                Description
              </label>
              <textarea
                id="cc-event-description"
                name="description"
                rows={3}
                value={formData.description}
                onChange={handleChange}
                placeholder="Add details about this event…"
              />
            </div>

            <div className="field">
              <label className="field-label" htmlFor="cc-event-date">
                <FaCalendarAlt size={11} style={{ marginRight: 6, opacity: 0.75 }} />
                Date <span className="req">*</span>
              </label>
              <input
                id="cc-event-date"
                type="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                required
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-3)' }}>
              <div className="field">
                <label className="field-label" htmlFor="cc-event-start">
                  <FaClock size={11} style={{ marginRight: 6, opacity: 0.75 }} />
                  Start Time
                </label>
                <input
                  id="cc-event-start"
                  type="time"
                  name="startTime"
                  value={formData.startTime}
                  onChange={handleChange}
                />
              </div>
              <div className="field">
                <label className="field-label" htmlFor="cc-event-end">
                  <FaClock size={11} style={{ marginRight: 6, opacity: 0.75 }} />
                  End Time
                </label>
                <input
                  id="cc-event-end"
                  type="time"
                  name="endTime"
                  value={formData.endTime}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="field" style={{ marginBottom: 0 }}>
              <label className="field-label" htmlFor="cc-event-location">
                <FaMapMarkerAlt size={11} style={{ marginRight: 6, opacity: 0.75 }} />
                Location
              </label>
              <input
                id="cc-event-location"
                type="text"
                name="location"
                value={formData.location}
                onChange={handleChange}
                placeholder="e.g. Main Hall"
              />
            </div>
          </div>

          <div className="modal-foot">
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={isSaving}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={isSaving}>
              {isSaving ? (
                <>
                  <span className="spinner" /> Saving…
                </>
              ) : (
                'Save Event'
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

const EventActionsMenu = ({ event, onEdit, onDelete }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={menuRef} style={{ flexShrink: 0 }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="icon-btn"
        style={{ width: 26, height: 26 }}
        aria-label="Event actions"
        aria-haspopup="menu"
        aria-expanded={isOpen}
      >
        <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h.01M12 12h.01M19 12h.01M6 12a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0z" />
        </svg>
      </button>

      {isOpen && (
        <div
          role="menu"
          className="panel absolute right-0 mt-1"
          style={{ minWidth: 130, overflow: 'hidden', zIndex: 20, boxShadow: '0 20px 50px -20px rgba(0,0,0,.7)' }}
        >
          <button
            type="button"
            role="menuitem"
            className="w-full text-left"
            style={{ padding: '9px 12px', fontSize: 12.5, color: 'var(--text-1)', background: 'transparent' }}
            onClick={() => { setIsOpen(false); onEdit(event); }}
          >
            Edit
          </button>
          <button
            type="button"
            role="menuitem"
            className="w-full text-left"
            style={{ padding: '9px 12px', fontSize: 12.5, color: 'var(--error)', background: 'transparent', borderTop: '1px solid var(--border)' }}
            onClick={() => { setIsOpen(false); onDelete(event); }}
          >
            Delete
          </button>
        </div>
      )}
    </div>
  );
};

export default function ClubCalander() {
  // Get user info from localStorage
  const [user, setUser] = useState(
    JSON.parse(localStorage.getItem('user')) || {
      name: 'Unknown User',
      role: 'Member',
      image: '/user.png',
    }
  );
  // Create/Edit/Delete previously had no gate beyond page access. Fallback
  // is this page's own (very broad) route access list, so nothing anyone
  // could already do stops working until an admin explicitly narrows it.
  const calendarPerm = useRolePermissions(user.role);
  const canManageCalendar = hasPermission(
    'calendar_manage',
    calendarPerm,
    user.role,
    ['Club Performence Evaluator', 'System Admin', 'President', 'Vice President', 'Secretary', 'Treasurer', 'Co - Asst. Secretary', 'Co - Asst. Treasurer', 'Chief Editor', 'Past President', 'President Program Coordinator', 'Activity Director', 'Club Director', 'Past Board Member', 'Team Head', 'IT Department', 'Installation sub-committee member', 'Reporting Sub-Committee', 'Committee Member', 'Club Member']
  );
  const [date, setDate] = useState(new Date());
  const [showEventModal, setShowEventModal] = useState(false);
  const [events, setEvents] = useState([]);
  const [birthdayEvents, setBirthdayEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);

  // State for error handling
  const [error, setError] = useState(null);

  // Form state for event modal
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    startTime: '09:00',
    endTime: '10:00',
    location: '',
    color: colorOptions[Math.floor(Math.random() * colorOptions.length)],
    all_day: false
  });

  // Refs
  const eventFormRef = useRef(null);

  // Fetch events from the API
  const loadEvents = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Get the first and last day of the current month
      const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
      const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);

      // Fetch events for the current month
      const eventsData = await fetchEvents(firstDay, lastDay);
      setEvents(eventsData);

    } catch (error) {
      console.error('Error loading events:', error);
      setError('Failed to load events. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [date]);

  // Fetch birthdays
  const loadBirthdays = useCallback(async () => {
    try {
      const members = await fetchBirthdays();
      if (members.length === 0) {
        setError(prev => prev || 'No birthdays found or failed to load birthdays');
      }
      const birthdays = getBirthdayEvents(members);
      setBirthdayEvents(birthdays);
    } catch (error) {
      console.error('Error loading birthdays:', error);
      setError('Failed to load birthdays. Please try again.');
    }
  }, []);

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      await Promise.all([
        loadEvents(),
        loadBirthdays()
      ]);
    };

    loadData();
  }, [loadEvents, loadBirthdays]);

  // Combine regular events with birthdays
  const allEvents = useMemo(() => {
    return [...events, ...birthdayEvents];
  }, [events, birthdayEvents]);

  const handleAddEvent = async (newEvent) => {
    try {
      setIsSaving(true);

      // Format the event data for the API
      const eventData = {
        ...newEvent,
        start_date: format(new Date(newEvent.date), 'yyyy-MM-dd'),
        start_time: newEvent.startTime,
        end_time: newEvent.endTime,
        color: newEvent.color || 'bg-violet-500'
      };

      let result;

      if (editingEvent && editingEvent.id) {
        // Update existing event
        const updatedEvent = await updateEvent(editingEvent.id, eventData);

        // Update the local state with the updated event
        setEvents(prev => prev.map(e =>
          e.id === editingEvent.id
            ? {
                ...updatedEvent,
                date: new Date(updatedEvent.start_date),
                time: updatedEvent.all_day
                  ? 'All Day'
                  : `${updatedEvent.start_time} - ${updatedEvent.end_time}`.trim(),
                dotColor: getContrastColor(updatedEvent.color || 'bg-violet-500')
              }
            : e
        ));

        result = true;
        setError({ type: 'success', message: 'Event updated successfully' });
      } else if (!editingEvent) {
        // Create new event
        const createdEvent = await createEvent(eventData);

        // Update the local state with the new event
        setEvents(prev => [...prev, {
          ...createdEvent,
          date: new Date(createdEvent.start_date),
          time: createdEvent.all_day
            ? 'All Day'
            : `${createdEvent.start_time} - ${createdEvent.end_time}`.trim(),
          dotColor: getContrastColor(createdEvent.color || 'bg-violet-500')
        }]);

        result = true;
        setError({ type: 'success', message: 'Event created successfully' });
      } else {
        setError({ type: 'error', message: 'Cannot update event: missing event ID.' });
        return false;
      }

      // Clear any previous errors
      setTimeout(() => setError(null), 3000);
      return result;

    } catch (error) {
      console.error('Error saving event:', error);
      setError({
        type: 'error',
        message: error.message || 'Failed to save event. Please try again.'
      });
      return false;
    } finally {
      setIsSaving(false);
    }
  };


  // Helper to safely format dates for input fields
  const safeFormatDate = (date) => {
    if (!date) return '';
    const d = (date instanceof Date) ? date : new Date(date);
    if (isNaN(d.getTime())) return '';
    return format(d, 'yyyy-MM-dd');
  };

  const openEventModal = (event = null) => {
    if (event) {
      setEditingEvent(event);
      // Pre-fill the form with event data
      setFormData({
        title: event.title,
        description: event.description || '',
        date: safeFormatDate(event.date),
        startTime: event.start_time || '09:00',
        endTime: event.end_time || '10:00',
        location: event.location || '',
        color: event.color || 'bg-violet-500'
      });
    } else {
      setEditingEvent(null);
      // Reset form data
      setFormData({
        title: '',
        description: '',
        date: format(date, 'yyyy-MM-dd'),
        startTime: '09:00',
        endTime: '10:00',
        location: '',
        color: colorOptions[Math.floor(Math.random() * colorOptions.length)]
      });
    }
    setShowEventModal(true);
  };

  const handleEditEvent = (event) => {
    openEventModal(event);
  };

  const handleDeleteEvent = async (event) => {
    if (window.confirm(`Are you sure you want to delete "${event.title}"?`)) {
      try {
        setIsLoading(true);
        await deleteEvent(event.id);

        // Update local state
        setEvents(prev => prev.filter(e => e.id !== event.id));

        // Show success message
        setError({ type: 'success', message: 'Event deleted successfully' });
        setTimeout(() => setError(null), 3000);
      } catch (error) {
        console.error('Error deleting event:', error);
        setError({ type: 'error', message: error.message || 'Failed to delete event' });
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Filter events for the selected month/year
  const monthEvents = allEvents.filter(e =>
    e.date.getFullYear() === date.getFullYear() && e.date.getMonth() === date.getMonth()
  );

  // Get upcoming birthdays (next 30 days)
  const upcomingBirthdays = useMemo(() => {
    const today = new Date();
    const nextMonth = new Date(today);
    nextMonth.setMonth(today.getMonth() + 1);

    return birthdayEvents
      .filter(event => {
        const eventDate = new Date(event.date);
        eventDate.setFullYear(today.getFullYear()); // Normalize to current year for comparison
        return eventDate >= today && eventDate <= nextMonth;
      })
      .sort((a, b) => a.date - b.date);
  }, [birthdayEvents]);

  return (
    <div className="min-h-screen">
      {/* Scoped keyframes: kept local to this page instead of being injected into
          document.head globally (the previous approach leaked a permanent
          ::-webkit-scrollbar override onto every page in the app). Only the
          gradient animation used by getDateStyle() below is needed here. */}
      <style>{`
        @keyframes gradient {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>

      <Sidebar />
      <Topbar user={user} />
      <main className="lg:ml-64 mt-14 px-4 sm:px-6 md:px-8 py-5 md:py-10">
        {/* Error/Success message */}
        {error && (
          <div
            className={error.type === 'error' ? 'error-banner mb-4' : 'success-banner mb-4'}
            style={{ justifyContent: 'space-between', alignItems: 'center' }}
          >
            <span>{error.message}</span>
            <button
              onClick={() => setError(null)}
              className="icon-btn"
              style={{ width: 24, height: 24, flexShrink: 0 }}
              aria-label="Dismiss"
            >
              <FaTimes size={12} />
            </button>
          </div>
        )}

        {/* Loading overlay */}
        {isLoading && (
          <div className="modal-overlay" style={{ zIndex: 1000 }}>
            <div className="panel" style={{ padding: 'var(--sp-5) var(--sp-6)', display: 'flex', alignItems: 'center', gap: 14 }}>
              <span className="spinner lg" />
              <span style={{ color: 'var(--text-1)', fontWeight: 600, fontSize: 13.5 }}>Loading calendar…</span>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="page-head">
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-4)' }}>
            <span
              style={{
                width: 44, height: 44, borderRadius: 'var(--radius-md)', flexShrink: 0,
                background: 'linear-gradient(155deg, var(--accent), var(--accent-secondary))',
                display: 'grid', placeItems: 'center',
                boxShadow: '0 12px 28px -12px var(--glow-1, rgba(124,58,237,.55))'
              }}
            >
              <FaLaptop color="#fff" size={18} />
            </span>
            <div>
              <h1>Club Calendar</h1>
              <p className="page-desc">Manage your schedule and events</p>
            </div>
          </div>

          {canManageCalendar && (
          <button type="button" className="btn btn-primary" onClick={() => openEventModal()}>
            <FaPlus size={12} /> Add Event
          </button>
          )}
        </div>

        <div className="split">
          {/* Calendar Section (main column) */}
          <div className="panel">
            <div className="section-head" style={{ flexWrap: 'wrap', rowGap: 'var(--sp-3)' }}>
              <h2>
                {date.toLocaleString('default', { month: 'long' })}{' '}
                <span className="num" style={{ fontWeight: 400, color: 'var(--text-3)' }}>{date.getFullYear()}</span>
              </h2>

              <div className="filter-row" style={{ flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 2, background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: 3 }}>
                  <button
                    type="button"
                    onClick={() => {
                      const newDate = new Date(date);
                      newDate.setMonth(newDate.getMonth() - 1);
                      setDate(newDate);
                    }}
                    className="icon-btn"
                    style={{ width: 26, height: 26 }}
                    aria-label="Previous month"
                  >
                    <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => setDate(new Date())}
                    className="btn btn-ghost btn-sm"
                    style={{ padding: '5px 10px' }}
                  >
                    Today
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const newDate = new Date(date);
                      newDate.setMonth(newDate.getMonth() + 1);
                      setDate(newDate);
                    }}
                    className="icon-btn"
                    style={{ width: 26, height: 26 }}
                    aria-label="Next month"
                  >
                    <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>

                <button type="button" className="chip active">Month</button>
                <button type="button" className="chip">Week</button>
                <button type="button" className="chip">Day</button>
              </div>
            </div>

            {/* Calendar Grid */}
            <div style={{ padding: '0 var(--sp-4) var(--sp-4)' }}>
              <div className="grid grid-cols-7" style={{ borderBottom: '1px solid var(--border)' }}>
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                  <div key={day} className="eyebrow" style={{ textAlign: 'center', padding: '10px 0' }}>
                    {day}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-7">
                {getCalendarDays(date).map((day, index) => {
                  const dayEvents = allEvents.filter(event => isSameDay(event.date, day.date));
                  const isCurrentMonth = day.isCurrentMonth;
                  const isToday = isSameDay(day.date, new Date());
                  const isSelected = isSameDay(day.date, date);
                  const dateStyle = getDateStyle(day.date, allEvents);
                  const hasBirthday = dayEvents.some(event => event.isBirthday);

                  return (
                    <div
                      key={index}
                      className="min-h-[56px] sm:min-h-[76px] p-1 sm:p-2 text-xs sm:text-sm"
                      style={{
                        position: 'relative',
                        borderBottom: '1px solid var(--border)',
                        borderRight: '1px solid var(--border)',
                        color: isCurrentMonth ? 'var(--text-1)' : 'var(--text-3)',
                        cursor: 'pointer',
                        ...(isCurrentMonth ? dateStyle : {}),
                        background: isCurrentMonth && dateStyle?.background ? dateStyle.background : (isCurrentMonth ? (dateStyle?.backgroundColor || 'transparent') : 'transparent'),
                        outline: isSelected ? '2px solid var(--accent)' : 'none',
                        outlineOffset: -2,
                        zIndex: isSelected ? 2 : (dateStyle?.zIndex || 0),
                      }}
                      onClick={() => setDate(day.date)}
                    >
                      <div className="flex flex-col h-full">
                        <div className="relative flex items-center justify-center sm:justify-start">
                          <span
                            className="inline-flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 rounded-full text-xs sm:text-sm font-medium"
                            style={
                              isToday && isCurrentMonth
                                ? { background: 'var(--accent)', color: '#fff' }
                                : isSelected
                                  ? { color: 'var(--text-1)', fontWeight: 600 }
                                  : undefined
                            }
                          >
                            {day.date.getDate()}
                          </span>
                          {hasBirthday && (
                            <span
                              className="absolute -top-1 -right-1 w-2 h-2 rounded-full"
                              style={{ background: 'var(--accent-dark)' }}
                            />
                          )}
                        </div>

                        {/* Event Dots */}
                        {dayEvents.length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-0.5 sm:gap-1 justify-center sm:justify-start">
                            {dayEvents.slice(0, 3).map((event, i) => (
                              <div
                                key={i}
                                className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full flex-shrink-0"
                                style={{ backgroundColor: colorMap[event.color] || '#6366F1' }}
                                title={event.title}
                              ></div>
                            ))}
                            {dayEvents.length > 3 && (
                              <span className="text-[10px] sm:text-xs" style={{ color: 'var(--text-3)' }}>+{dayEvents.length - 3}</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Upcoming Events Section (aside) */}
          <div className="panel">
            <div className="section-head">
              <h2>
                {date.toLocaleString('default', { month: 'long' })}{' '}
                <span className="num" style={{ fontWeight: 400, color: 'var(--text-3)' }}>{date.getFullYear()}</span>
              </h2>
              <button type="button" className="icon-btn" aria-label="More options">
                <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                </svg>
              </button>
            </div>

            {(upcomingBirthdays.length > 0 || monthEvents.length > 0) ? (
              <div className="timeline">
                {upcomingBirthdays.length > 0 && (
                  <>
                    <div className="eyebrow" style={{ padding: '2px 0 10px' }}>Upcoming Birthdays</div>
                    {upcomingBirthdays.slice(0, 3).map((event, index) => {
                      const member = event.member || {};
                      return (
                        <div className="t-item" key={`bday-${index}`}>
                          <div className="t-date">
                            <span className="d">{event.date.getDate()}</span>
                            <span className="m">{event.date.toLocaleDateString('en-US', { month: 'short' })}</span>
                          </div>
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div className="t-title truncate">{member.name || event.title}</div>
                            <div className="t-meta truncate">
                              {member.DOB
                                ? new Date(member.DOB).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
                                : event.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              {member.role ? ` • ${member.role}` : ''}
                            </div>
                          </div>
                          <span className="pill pill-info" style={{ flexShrink: 0 }}>
                            <span className="dot" /> Birthday
                          </span>
                        </div>
                      );
                    })}
                  </>
                )}

                <div className="eyebrow" style={{ padding: upcomingBirthdays.length > 0 ? '14px 0 10px' : '2px 0 10px' }}>
                  Upcoming Events
                </div>

                {monthEvents.length > 0 ? (
                  monthEvents.map((event, index) => (
                    <div className="t-item" key={index}>
                      <div className="t-date">
                        <span className="d">{event.date.getDate().toString().padStart(2, '0')}</span>
                        <span className="m">{event.date.toLocaleDateString('en-US', { month: 'short' })}</span>
                      </div>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div className="t-title truncate" title={event.title}>{event.title}</div>
                        {event.isBirthday && event.member ? (
                          <div className="t-meta truncate">
                            {event.member.name}
                            {event.member.role ? ` • ${event.member.role}` : ''}
                          </div>
                        ) : (
                          <div className="t-meta truncate">
                            {event.date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                            {event.description ? ` • ${event.description}` : ''}
                          </div>
                        )}
                      </div>
                      <span
                        style={{ width: 8, height: 8, borderRadius: 999, background: colorMap[event.color] || 'var(--accent)', flexShrink: 0 }}
                        title={event.title}
                      />
                      {canManageCalendar && (
                      <EventActionsMenu
                        event={event}
                        onEdit={handleEditEvent}
                        onDelete={handleDeleteEvent}
                      />
                      )}
                    </div>
                  ))
                ) : (
                  <div style={{ padding: '10px 0', fontSize: 12.5, color: 'var(--text-3)' }}>
                    No events this month.
                  </div>
                )}
              </div>
            ) : (
              <div className="empty-state">
                <div className="empty-icon">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3>No events this month</h3>
                <p>Add an event to get started.</p>
                {canManageCalendar && (
                <button type="button" className="btn btn-primary btn-sm" style={{ marginTop: 4 }} onClick={() => openEventModal()}>
                  <FaPlus size={11} /> Add Event
                </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Event Modal */}
        <AnimatePresence>
          {showEventModal && (
            <EventModal
              isOpen={showEventModal}
              onClose={() => setShowEventModal(false)}
              onSave={async (event) => {
                const success = await handleAddEvent(event);
                if (success) {
                  setShowEventModal(false);
                }
              }}
              isSaving={isSaving}
              selectedDate={date}
            />
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}
